import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Zap, Volume2, VolumeX } from "lucide-react";
import api from "../lib/api";

let hrAudio;
function playCrack() {
  try {
    hrAudio = hrAudio || new Audio("/sounds/hr.mp3");
    hrAudio.currentTime = 0;
    hrAudio.volume = 0.85;
    hrAudio.play().catch(() => {});
  } catch (e) {
    /* audio unavailable */
  }
}

function fireConfetti() {
  const colors = ["#a855f7", "#ffffff", "#22c55e", "#eab308"];
  confetti({ particleCount: 120, spread: 75, origin: { x: 0.5, y: 0.7 }, colors, zIndex: 9999 });
  setTimeout(() => confetti({ particleCount: 70, angle: 60, spread: 60, origin: { x: 0, y: 0.8 }, colors, zIndex: 9999 }), 200);
  setTimeout(() => confetti({ particleCount: 70, angle: 120, spread: 60, origin: { x: 1, y: 0.8 }, colors, zIndex: 9999 }), 350);
}

export function HRCelebration() {
  const seen = useRef(null);
  const [overlay, setOverlay] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("hr_sound") !== "off");
  const { data } = useQuery({
    queryKey: ["hrAlerts"],
    queryFn: () => api.get("/hr-alerts").then((r) => r.data),
    refetchInterval: 90000,
    refetchIntervalInBackground: false,
  });

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("hr_sound", next ? "on" : "off");
    if (next) playCrack();
  };

  useEffect(() => {
    if (!data?.alerts) return;
    const keys = data.alerts.map((a) => a.key);
    if (seen.current === null) {
      seen.current = new Set(keys);
      return;
    }
    let fired = false;
    let last = null;
    for (const a of data.alerts) {
      if (seen.current.has(a.key)) continue;
      seen.current.add(a.key);
      fired = true;
      last = a;
      toast(
        <div className="flex items-center gap-3" data-testid="hr-celebration-toast">
          <div className="w-9 h-9 rounded-full bg-[#a855f7]/20 border border-[#a855f7] flex items-center justify-center shrink-0 animate-bounce">
            <Zap size={16} className="text-[#a855f7]" />
          </div>
          <div>
            <div className="font-display font-black uppercase text-[#a855f7] tracking-wide">HOME RUN!</div>
            <div className="text-sm text-white font-semibold">{a.batter} just went yard</div>
            <div className="text-xs text-neutral-400">{a.team} · {a.inning}{a.rbi > 1 ? ` · ${a.rbi} RBI` : ""}</div>
          </div>
        </div>,
        { duration: 10000 }
      );
    }
    if (fired) {
      fireConfetti();
      setOverlay(last);
      setTimeout(() => setOverlay(null), 4200);
      if (localStorage.getItem("hr_sound") !== "off") playCrack();
    }
  }, [data]);

  return (
    <>
      {overlay && (
        <div className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center" data-testid="hr-overlay">
          <div className="hr-overlay-text text-center relative px-6">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} className="hr-spark" style={{
                left: "50%", top: "50%",
                "--sx": `${Math.cos((i / 16) * Math.PI * 2) * (120 + (i % 3) * 60)}px`,
                "--sy": `${Math.sin((i / 16) * Math.PI * 2) * (90 + (i % 4) * 45)}px`,
                animationDelay: `${(i % 5) * 0.08}s`,
              }} />
            ))}
            <div className="font-display text-2xl sm:text-4xl font-black uppercase text-white tracking-wide" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.9)" }}>{overlay.batter}</div>
            <div className="font-chalk text-6xl sm:text-8xl gold-text leading-none mt-1">HOME RUN!</div>
            <div className="text-sm text-neutral-200 font-semibold mt-2" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.9)" }}>{overlay.team} · {overlay.inning}{overlay.rbi > 1 ? ` · ${overlay.rbi} RBI` : ""}</div>
          </div>
        </div>
      )}
      <button
      onClick={toggleSound}
      data-testid="hr-sound-toggle"
      title={soundOn ? "HR alert sound: ON (click to mute)" : "HR alert sound: OFF (click to unmute)"}
      className={`fixed bottom-20 lg:bottom-4 right-4 z-40 flex items-center gap-1.5 rounded-full border px-3 py-2 text-[10px] uppercase tracking-widest font-bold backdrop-blur-md transition-colors duration-200 ${
        soundOn ? "border-[#a855f7]/60 bg-[#a855f7]/15 text-[#a855f7]" : "border-white/15 bg-black/60 text-neutral-500 hover:text-neutral-300"
      }`}
    >
      {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />} HR Sound
      </button>
    </>
  );
}
